import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { fmtAge, fmtMoney } from "../lib/format";
import type { EaSummary, OverviewPayload } from "../lib/types";
import { Card, CardTitle, Pill } from "../components/Card";

export default function OverviewPage() {
  const overview = useQuery({
    queryKey: ["overview"],
    queryFn: () => api.get<OverviewPayload>("/admin/api/overview"),
    refetchInterval: 3_000,
    refetchIntervalInBackground: true,
  });

  const [group, setGroup] = useState<"day" | "week" | "month" | "year">("day");
  const todayStr = ymdLocal(new Date());
  // Default range = this week (Monday → Sunday).
  const [fromStr, setFromStr] = useState(() => ymdLocal(startOfWeek(new Date())));
  const [toStr, setToStr] = useState(() => ymdLocal(endOfWeek(new Date())));
  // Parse the picker dates as UTC, not local. The server buckets heartbeats by
  // UTC calendar day, so sending local midnight (17:00Z the previous day in
  // VN) shifted the whole window back and produced a phantom leading bucket
  // for a date the user never asked for.
  const fromMs = Date.parse(fromStr + "T00:00:00Z");
  const toMs = Date.parse(toStr + "T00:00:00Z") + 86400000;
  const realizedPnl = useQuery({
    queryKey: ["pnl-buckets", group, fromMs, toMs],
    queryFn: () =>
      api.get<{ buckets: Array<{ label: string; pnlTotal: number; eaCount: number }> }>(
        `/admin/api/pnl-buckets?group=${group}&from=${fromMs}&to=${toMs}`,
      ),
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  });

  if (overview.isLoading) return <div className="text-muted">Loading…</div>;
  if (overview.error || !overview.data) return <ErrorBlock err={overview.error} />;

  const { eas } = overview.data;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-on-dark sm:text-display-sm">Dashboard</h1>
        <p className="mt-1 text-sm text-muted">Live snapshot · gộp tất cả account</p>
      </header>

      {/* Realized P&L chart + Portfolio summary — side by side on lg, stacked on mobile */}
      <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardTitle
            right={
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-1 rounded-md bg-surface-elevated p-0.5 text-xs">
                  {(["day", "week", "month", "year"] as const).map((g) => (
                    <button
                      key={g}
                      type="button"
                      onClick={() => setGroup(g)}
                      className={`rounded px-2.5 py-1 transition ${
                        group === g
                          ? "bg-primary text-on-primary font-semibold"
                          : "text-muted hover:text-on-dark"
                      }`}
                    >
                      {g === "day" ? "Ngày" : g === "week" ? "Tuần" : g === "month" ? "Tháng" : "Năm"}
                    </button>
                  ))}
                </div>
                <input
                  type="date"
                  value={fromStr}
                  max={toStr}
                  onChange={(e) => setFromStr(e.target.value)}
                  className="rounded-md border border-hairline-dark bg-canvas-dark px-2 py-1 text-xs text-on-dark"
                />
                <span className="text-muted">→</span>
                <input
                  type="date"
                  value={toStr}
                  min={fromStr}
                  onChange={(e) => setToStr(e.target.value)}
                  className="rounded-md border border-hairline-dark bg-canvas-dark px-2 py-1 text-xs text-on-dark"
                />
              </div>
            }
          >
            Realized P&L · USD
          </CardTitle>
          <RealizedPnlChart rows={realizedPnl.data?.buckets ?? []} today={todayStr} />
        </Card>

        <Card>
          <CardTitle right="live · gộp tất cả account">Tổng quan tài khoản</CardTitle>
          <PortfolioSummary eas={eas} />
        </Card>
      </section>

      <Card>
        <CardTitle right={`${eas.length} account · live`}>Danh sách tài khoản</CardTitle>
        <AccountsList eas={eas} />
      </Card>
    </div>
  );
}

function AccountsList({ eas }: { eas: EaSummary[] }) {
  return (
    <div className="-mx-5 overflow-x-auto px-5">
      <table className="w-full min-w-[640px] text-sm">
        <thead className="text-left text-[11px] uppercase tracking-wider text-muted">
          <tr>
            <th className="py-2">Account</th>
            <th className="text-right">Balance</th>
            <th className="text-right">Realized today</th>
            <th className="text-right">Unrealized</th>
            <th className="text-right">P&L today</th>
            <th className="text-right">Open</th>
            <th className="text-right">Last</th>
          </tr>
        </thead>
        <tbody>
          {eas.map((e) => {
            const isCent = (e.currency ?? "").toUpperCase() === "USC";
            const realized = e.pnlRealizedTodayUsd ?? 0;
            const floating = e.equityUsd - e.balanceUsd;
            return (
              <tr key={e.eaId} className="border-t border-hairline-dark hover:bg-surface-elevated/50">
                <td className="py-2.5">
                  <Link className="font-mono text-on-dark hover:text-primary" to={`/eas/${encodeURIComponent(e.eaId)}`}>
                    {e.eaId}
                  </Link>
                  {isCent ? (
                    <span className="ml-2">
                      <Pill tone="warn">CENT</Pill>
                    </span>
                  ) : null}
                </td>
                <td className="text-right font-mono">{fmtMoney(e.balanceUsd)}</td>
                <td className={`text-right font-mono ${realized < 0 ? "text-trading-down" : realized > 0 ? "text-trading-up" : "text-muted"}`}>
                  {fmtMoney(realized)}
                </td>
                <td className={`text-right font-mono ${floating < 0 ? "text-trading-down" : floating > 0 ? "text-trading-up" : "text-muted"}`}>
                  {fmtMoney(floating)}
                </td>
                <td className={`text-right font-mono ${e.pnlTodayUsd < 0 ? "text-trading-down" : e.pnlTodayUsd > 0 ? "text-trading-up" : "text-muted"}`}>
                  {fmtMoney(e.pnlTodayUsd)}
                </td>
                <td className="text-right font-mono">{e.openPositions}</td>
                <td className="text-right">
                  <Pill tone={e.staleSeconds <= 90 ? "good" : e.staleSeconds <= 600 ? "warn" : "bad"}>
                    {fmtAge(e.staleSeconds)}
                  </Pill>
                </td>
              </tr>
            );
          })}
          {eas.length === 0 ? (
            <tr>
              <td colSpan={7} className="py-6 text-center text-muted">No accounts</td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

function PortfolioSummary({ eas }: { eas: EaSummary[] }) {
  const totalBal = eas.reduce((s, e) => s + e.balanceUsd, 0);
  const totalEq = eas.reduce((s, e) => s + e.equityUsd, 0);
  const realized = eas.reduce((s, e) => s + (e.pnlRealizedTodayUsd ?? 0), 0);
  const floating = totalEq - totalBal;
  const pnlToday = eas.reduce((s, e) => s + e.pnlTodayUsd, 0);
  const xauBuy = eas.reduce((s, e) => s + e.buyLotsXau, 0);
  const xauSell = eas.reduce((s, e) => s + e.sellLotsXau, 0);
  const btcBuy = eas.reduce((s, e) => s + e.buyLotsBtc, 0);
  const btcSell = eas.reduce((s, e) => s + e.sellLotsBtc, 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Metric label="Total Balance" value={fmtMoney(totalBal)} />
        <Metric label="Total Equity" value={fmtMoney(totalEq)} />
        <Metric label="Realized Today" value={fmtMoney(realized)} tone={realized < 0 ? "down" : "up"} />
        <Metric label="Floating" value={fmtMoney(floating)} tone={floating < 0 ? "down" : "up"} />
        <Metric label="P&L Today" value={fmtMoney(pnlToday)} tone={pnlToday < 0 ? "down" : "up"} />
      </div>
      <div className="rounded-md border border-hairline-dark bg-canvas-dark/40 px-3 py-2">
        <div className="mb-1 text-[11px] uppercase tracking-wider text-muted">Net Lot (buy / sell · net)</div>
        <LotRow sym="XAU" buy={xauBuy} sell={xauSell} />
        <LotRow sym="BTC" buy={btcBuy} sell={btcSell} />
      </div>
    </div>
  );
}

function Metric({ label, value, tone }: { label: string; value: string; tone?: "up" | "down" }) {
  const c = tone === "up" ? "text-trading-up" : tone === "down" ? "text-trading-down" : "text-on-dark";
  return (
    <div className="rounded-md border border-hairline-dark bg-canvas-dark/40 px-3 py-2">
      <div className="text-[11px] uppercase tracking-wider text-muted">{label}</div>
      <div className={`font-mono text-lg ${c}`}>{value}</div>
    </div>
  );
}

function LotRow({ sym, buy, sell }: { sym: string; buy: number; sell: number }) {
  const net = buy - sell;
  return (
    <div className="flex items-center gap-3 py-0.5 font-mono text-sm">
      <span className="w-8 text-muted">{sym}</span>
      <span className="text-trading-up">{buy.toFixed(2)}</span>
      <span className="text-muted">/</span>
      <span className="text-trading-down">{sell.toFixed(2)}</span>
      <span className={`ml-auto ${net > 0 ? "text-trading-up" : net < 0 ? "text-trading-down" : "text-muted"}`}>
        {net > 0 ? "+" : ""}
        {net.toFixed(2)}
      </span>
    </div>
  );
}

// Realized P&L column chart by bucket (day/week/month/year). Bars grow up (win)
// or down (loss) from a midline.
function RealizedPnlChart({
  rows,
  today,
}: {
  rows: Array<{ label: string; pnlTotal: number; eaCount: number }>;
  today: string;
}) {
  if (rows.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-muted">No realized P&L data for this range.</div>
    );
  }
  const maxAbs = Math.max(1, ...rows.map((r) => Math.abs(r.pnlTotal)));
  const total = rows.reduce((s, r) => s + r.pnlTotal, 0);
  const winDays = rows.filter((r) => r.pnlTotal > 0).length;
  const lossDays = rows.filter((r) => r.pnlTotal < 0).length;
  const flatDays = rows.filter((r) => r.pnlTotal === 0).length;
  const CHART_H = 160;
  // The series is now zero-filled, so a month is 30+ bars instead of however
  // many happened to have data. Shrink the slot and thin the tick labels so a
  // full month fits without the axis turning into a smear.
  const dense = rows.length > 16;
  const slotPx = rows.length > 40 ? 16 : dense ? 22 : 36;
  const labelEvery = rows.length > 40 ? 5 : rows.length > 24 ? 3 : rows.length > 16 ? 2 : 1;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-baseline gap-x-6 gap-y-1 text-xs text-muted">
        <span>
          Total{" "}
          <span className={`font-mono ${total > 0 ? "text-trading-up" : total < 0 ? "text-trading-down" : "text-on-dark"}`}>
            {fmtMoney(total)}
          </span>
        </span>
        <span>Win <span className="font-mono text-trading-up">{winDays}</span></span>
        <span>Loss <span className="font-mono text-trading-down">{lossDays}</span></span>
        <span>Trống <span className="font-mono text-muted">{flatDays}</span></span>
      </div>

      <div className="-mx-1 overflow-x-auto px-1">
        <div
          className={`flex items-stretch ${dense ? "gap-0.5" : "gap-1.5"}`}
          style={{ minWidth: `${rows.length * slotPx}px` }}
        >
          {rows.map((r, idx) => {
            const heightPx = (Math.abs(r.pnlTotal) / maxAbs) * (CHART_H / 2 - 4);
            const isUp = r.pnlTotal > 0;
            const isDown = r.pnlTotal < 0;
            const isToday = r.label === today;
            return (
              <div
                key={r.label}
                className={`flex flex-1 flex-col items-center gap-1 ${
                  isToday ? "rounded-md ring-1 ring-primary/40 bg-primary/[0.04]" : ""
                }`}
                title={`${r.label} — ${fmtMoney(r.pnlTotal)} · ${r.eaCount} EA`}
              >
                <div className={`h-4 text-[10px] font-mono leading-4 ${isUp ? "text-trading-up" : isDown ? "text-trading-down" : "text-muted"}`}>
                  {!dense && Math.abs(r.pnlTotal) >= 0.5
                    ? (r.pnlTotal > 0 ? "+" : "") + Math.round(r.pnlTotal)
                    : ""}
                </div>
                <div className="relative w-full" style={{ height: `${CHART_H}px` }}>
                  <div className="absolute inset-x-0 top-1/2 h-px bg-hairline-dark" />
                  {isUp ? (
                    <div className="absolute left-1/2 -translate-x-1/2 rounded-t bg-trading-up/70 hover:bg-trading-up" style={{ bottom: "50%", height: `${heightPx}px`, width: "70%" }} />
                  ) : null}
                  {isDown ? (
                    <div className="absolute left-1/2 -translate-x-1/2 rounded-b bg-trading-down/70 hover:bg-trading-down" style={{ top: "50%", height: `${heightPx}px`, width: "70%" }} />
                  ) : null}
                </div>
                <div className={`h-4 whitespace-nowrap text-[10px] font-mono leading-4 ${isToday ? "font-semibold text-primary" : "text-muted"}`}>
                  {idx % labelEvery === 0 || isToday
                    ? r.label.length === 10
                      ? r.label.slice(dense ? 8 : 5)
                      : r.label
                    : ""}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ErrorBlock({ err }: { err: unknown }) {
  const msg = err instanceof Error ? err.message : String(err);
  return (
    <div className="rounded-md border border-trading-down/40 bg-trading-down/10 px-4 py-3 text-trading-down">
      Failed: {msg}
    </div>
  );
}

function ymdLocal(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}
function startOfWeek(d: Date): Date {
  const x = new Date(d);
  const dow = (x.getDay() + 6) % 7; // Monday = 0
  x.setDate(x.getDate() - dow);
  return x;
}
function endOfWeek(d: Date): Date {
  const x = startOfWeek(d);
  x.setDate(x.getDate() + 6);
  return x;
}
